/** Everything this account is currently putting somewhere, on one card.
 *
 * The spec's promise is that each of the three data types is opt-in and that
 * the driver's own laps are private by default; this card is where that
 * promise is checked. It answers five questions in one glance — how many
 * credentials exist, how many installations they cover, how much driving is
 * stored and for how long, how much of it is public, and whether anyone can
 * watch — and each answer that can be acted on is a link to the screen that
 * acts on it, rather than a number with an instruction beside it.
 *
 * The exception is the last of the five, which has no screen of its own. A
 * stream's visibility is one column and one `PATCH /api/me`, and the spectate
 * page tells a turned-away visitor that "a driver turns spectating on from
 * their profile" — so this is where that sentence has to come true. It is a
 * button rather than a link because there is nowhere further to go, and it
 * states the consequence rather than the field name: made public, the address
 * beside it works for anybody who has it, with no sign-in.
 *
 * Credit is the same kind of switch for the same reason. A circuit's public
 * page lists who surveyed it, and an account is "a contributor" there until
 * its owner says its display name may be printed — off by default, because
 * the privacy page promised a name is public only where its owner chose.
 *
 * Underneath is the same account seen from the community's side: the circuits
 * it has contributed to, with the metres that landed and where the latest
 * survey stands. A held row names its issue type, because "held" on its own
 * is the complaint the spec's issue queue exists to answer.
 */

import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { Card, EmptyState, ErrorNote, Kicker, Spinner } from '@/components/ui';
import { bytes, count, plural } from '@/lib/format';
import type { AccountStats } from '@/lib/types';
import {
  contributionColour,
  contributionLabel,
  type TrackContribution,
} from '@/components/profile/contributions';

export interface AccountSharesProps {
  stats: AccountStats;
  tokensLive: number;
  /** Turns the account's live stream public or private. Omitted on a screen
   *  that is only reporting, which leaves the row as plain text. */
  onSetLivePublic?: (next: boolean) => void;
  /** True while that request is in flight. */
  livePublicSaving?: boolean;
  /** Set when it failed; the rest of the card still stands. */
  livePublicError?: unknown;
  /** Whether the display name is printed beside this account's surveys. */
  creditPublic: boolean;
  /** Turns that on or off. Omitted on a screen that is only reporting. */
  onSetCreditPublic?: (next: boolean) => void;
  creditPublicSaving?: boolean;
  creditPublicError?: unknown;
  /** Null while the uploads they are derived from are still arriving. */
  contributions: TrackContribution[] | null;
  /** Set when that listing failed; the rest of the card still stands. */
  contributionsError?: unknown;
  onRetryContributions?: () => void;
}

/** One line of the key/value grid. */
function Row({ name, children }: { name: string; children: ReactNode }) {
  return (
    <>
      <span>{name}</span>
      <span style={{ textAlign: 'right' }}>{children}</span>
    </>
  );
}

/** The spectate URL as a path, which is how the design writes it, falling
 *  back to whatever the server sent if it is not a URL this browser can
 *  parse — a self-hosted `PUBLIC_URL` is a configured string, not a promise.
 *  Not `api.spectatePath`, which is the other direction: an account id into
 *  the address this is shortening. */
function displayPath(url: string): string {
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return url;
  }
}

export function AccountShares({
  stats,
  tokensLive,
  onSetLivePublic,
  livePublicSaving = false,
  livePublicError,
  creditPublic,
  onSetCreditPublic,
  creditPublicSaving = false,
  creditPublicError,
  contributions,
  contributionsError,
  onRetryContributions,
}: AccountSharesProps) {
  return (
    <Card as="section" label="What this account shares" style={{ gap: 12 }}>
      <Kicker>what this account shares</Kicker>

      <div
        className="tabular-nums"
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0,1fr) auto',
          gap: '8px 16px',
          fontSize: 13.5,
        }}
      >
        <Row name="Tokens">
          {count(tokensLive)} active · <Link to="/profile/tokens">manage →</Link>
        </Row>
        <Row name="Installations">{plural(stats.installations, 'source id')}</Row>
        <Row name="Sessions stored">
          {count(stats.sessions)} · {bytes(stats.session_bytes)} ·{' '}
          {count(stats.sessions_retention_days)}-day retention
        </Row>
        <Row name="Public sessions">{count(stats.public_sessions)}</Row>
        {/* Readable by whoever holds the link, and listed nowhere: as much
            "somewhere" as a public session is, so it is counted here too. */}
        <Row name="Shared by link">{count(stats.link_sessions ?? 0)}</Row>
        <Row name="Live stream">
          {stats.live_public ? (
            <>
              public ·{' '}
              <a href={stats.spectate_url} target="_blank" rel="noreferrer noopener">
                {displayPath(stats.spectate_url)}
              </a>
            </>
          ) : (
            <span style={{ color: 'var(--color-neutral-500)' }}>private</span>
          )}
          {onSetLivePublic ? (
            <>
              {' · '}
              <button
                type="button"
                className="btn btn-ghost"
                style={{ fontSize: 12, padding: '0 6px' }}
                disabled={livePublicSaving}
                aria-busy={livePublicSaving || undefined}
                onClick={() => onSetLivePublic(!stats.live_public)}
              >
                {livePublicSaving
                  ? 'saving…'
                  : stats.live_public
                    ? 'make private'
                    : 'make public'}
              </button>
            </>
          ) : null}
        </Row>
        <Row name="Track credit">
          {creditPublic ? (
            'by name'
          ) : (
            <span style={{ color: 'var(--color-neutral-500)' }}>anonymous</span>
          )}
          {onSetCreditPublic ? (
            <>
              {' · '}
              <button
                type="button"
                className="btn btn-ghost"
                style={{ fontSize: 12, padding: '0 6px' }}
                disabled={creditPublicSaving}
                aria-busy={creditPublicSaving || undefined}
                onClick={() => onSetCreditPublic(!creditPublic)}
              >
                {creditPublicSaving ? 'saving…' : creditPublic ? 'hide my name' : 'show my name'}
              </button>
            </>
          ) : null}
        </Row>
      </div>

      {onSetLivePublic ? (
        <p style={{ margin: 0, fontSize: 11.5, color: 'var(--color-neutral-600)' }}>
          {stats.live_public
            ? 'Anyone with the address can watch while you are driving. Nothing is kept once a ' +
              'stream ends unless you opted into recording.'
            : 'Only you can watch your stream. Overlays and race control can be given a ' +
              'live:read token instead of making it public.'}
        </p>
      ) : null}

      {livePublicError ? (
        <ErrorNote error={livePublicError} title="could not change your stream" compact />
      ) : null}

      {onSetCreditPublic ? (
        <p style={{ margin: 0, fontSize: 11.5, color: 'var(--color-neutral-600)' }}>
          {creditPublic
            ? 'Your display name is printed beside the surveys you contributed, on each ' +
              'circuit’s public page. Their dates could let somebody match your name to your ' +
              'installation id in the published files.'
            : 'Each circuit’s public page lists your surveys as “a contributor”, with the ' +
              'dates and no name.'}
        </p>
      ) : null}

      {creditPublicError ? (
        <ErrorNote error={creditPublicError} title="could not change your credit" compact />
      ) : null}

      <Kicker className="mt-1.5">contribution</Kicker>

      {contributionsError ? (
        <ErrorNote
          error={contributionsError}
          title="could not read your uploads"
          compact
          onRetry={onRetryContributions}
        />
      ) : contributions === null ? (
        <Spinner label="loading your uploads" />
      ) : contributions.length === 0 ? (
        <EmptyState title="nothing contributed yet" compact>
          Survey a circuit in the datalogger, confirm its layout, and turn Tracks sync on — the
          first bundle it sends shows up here.
        </EmptyState>
      ) : (
        <div>
          {contributions.map((row, index) => (
            <div
              key={row.official_id}
              className="tabular-nums"
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0,1fr) auto auto',
                gap: 12,
                alignItems: 'center',
                fontSize: 13,
                padding: '6px 0',
                background:
                  index === contributions.length - 1
                    ? undefined
                    : 'var(--row-rule) no-repeat bottom / 100% 1px',
              }}
            >
              <Link
                to={`/tracks/${encodeURIComponent(row.official_id)}`}
                className="truncate"
                title={row.track_name}
                style={{ textDecoration: 'none' }}
              >
                {row.track_name}
              </Link>
              <span style={{ color: 'var(--color-neutral-400)' }}>{count(row.metres)} m</span>
              <span
                style={{ color: contributionColour(row.status), whiteSpace: 'nowrap' }}
                title={row.reason || undefined}
              >
                {contributionLabel(row)}
              </span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default AccountShares;
